import { useEffect, useState } from "react";
import { ShieldCheck, Mail, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtLeft = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

function useOtpCountdown(expiresAt) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!expiresAt) { setLeft(null); return; }
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(expiresAt) - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  return left;
}

function OtpRecipient({ receipt, otpEmail, setOtpEmail }) {
  if (receipt.channel === "whatsapp" && receipt.player_phone) {
    return <p className="text-xs text-muted-foreground">Giocata via WhatsApp: il codice verrà inviato su WhatsApp al numero <span className="font-semibold text-foreground" data-testid="otp-known-phone">{receipt.player_phone}</span></p>;
  }
  if (receipt.player_email) {
    return <p className="text-xs text-muted-foreground">Il codice verrà inviato all'email del vincitore: <span className="font-semibold text-foreground" data-testid="otp-known-email">{receipt.player_email}</span></p>;
  }
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">Email del cliente (nessuna email registrata)</Label>
      <Input data-testid="otp-email-input" type="email" value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)} placeholder="cliente@esempio.it" />
    </div>
  );
}

function OtpRequest({ receipt, otpEmail, setOtpEmail, busy, sendOtp, haveOtp }) {
  return (
    <>
      <OtpRecipient receipt={receipt} otpEmail={otpEmail} setOtpEmail={setOtpEmail} />
      <Button data-testid="send-otp-button" onClick={sendOtp} disabled={busy} className="w-full gap-2 h-12">
        <Mail className="h-4 w-4" /> {busy ? "Invio…" : "Invia codice OTP"}
      </Button>
      <Button data-testid="have-otp-button" variant="secondary" onClick={haveOtp} disabled={busy} className="w-full h-10 text-xs">
        Il cliente ha già il codice (ricevuto scrivendo RITIRO su WhatsApp)
      </Button>
    </>
  );
}

function countdownClass(left) {
  if (left === 0) return "bg-red-500/15 text-red-300";
  if (left < 60) return "bg-amber-500/15 text-amber-300";
  return "bg-secondary/60";
}

function OtpEntry({ otpInfo, otp, setOtp, busy, redeem, sendOtp }) {
  const otpLeft = useOtpCountdown(otpInfo?.expires_at);
  const otpExpired = otpLeft === 0;
  return (
    <>
      <p className="text-xs text-muted-foreground" data-testid="otp-sent-info">
        {otpInfo?.via === "whatsapp"
          ? "Inserisci il codice a 6 cifre che il cliente ha ricevuto su WhatsApp scrivendo RITIRO."
          : <>Codice inviato a <span className="font-semibold text-foreground">{otpInfo?.email_masked || otpInfo?.phone_masked}</span> · valido {otpInfo?.expires_in_minutes} minuti. Chiedi al cliente il codice a 6 cifre.</>}
      </p>
      {otpLeft !== null && (
        <div className={`flex items-center justify-between rounded-md px-3 py-2 text-sm font-mono-code ${countdownClass(otpLeft)}`} data-testid="otp-countdown">
          <span>{otpExpired ? "OTP scaduto, chiedi al cliente di riscrivere RITIRO" : "Tempo rimanente OTP"}</span>
          {!otpExpired && <span className="font-bold text-lg">{fmtLeft(otpLeft)}</span>}
        </div>
      )}
      <Input data-testid="otp-input" inputMode="numeric" maxLength={6} value={otp} disabled={otpExpired} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && redeem()} placeholder="••••••" className="text-center text-2xl tracking-[0.6em] font-mono-code h-14" />
      <div className="flex gap-2">
        <Button data-testid="verify-redeem-button" onClick={redeem} disabled={busy || otpExpired} className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700 h-12">
          <CheckCircle2 className="h-4 w-4" /> Conferma ritiro
        </Button>
        <Button data-testid="resend-otp-button" variant="secondary" onClick={sendOtp} disabled={busy} className="h-12">Reinvia</Button>
      </div>
    </>
  );
}

export function OtpVerification({ receipt, store, busy, run, onRedeemed, resetKey, autoOtp = false }) {
  const [otpStep, setOtpStep] = useState("idle"); // idle | sent
  const [otp, setOtp] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [otpInfo, setOtpInfo] = useState(null);

  useEffect(() => { setOtpStep("idle"); setOtpEmail(""); setOtp(""); setOtpInfo(null); }, [resetKey]);

  const haveOtp = () => run(async () => {
    const st = await api.otpStatus(receipt.receipt_serial);
    if (!st.active) return toast.error("Nessun OTP attivo: chiedi al cliente di scrivere RITIRO su WhatsApp");
    setOtpInfo({ via: st.via, expires_at: st.expires_at });
    setOtpStep("sent");
    setOtp("");
  }, "Verifica OTP non riuscita");

  const sendOtp = () => {
    const viaWhatsApp = receipt.channel === "whatsapp" && receipt.player_phone;
    if (!viaWhatsApp && !receipt.player_email && !otpEmail) return toast.error("Inserisci l'email del cliente");
    return run(async () => {
      const r = await api.sendRedeemOtp(receipt.receipt_serial, otpEmail || null);
      setOtpInfo(r);
      setOtpStep("sent");
      setOtp("");
      toast.success(r.channel === "whatsapp" ? `Codice OTP inviato su WhatsApp a ${r.phone_masked}` : `Codice OTP inviato a ${r.email_masked}`);
    }, "Invio OTP non riuscito");
  };
  const canAuto = autoOtp && otpStep === "idle" && !receipt.redeemed && !receipt.expired && ((receipt.channel === "whatsapp" && receipt.player_phone) || receipt.player_email);
  useEffect(() => { if (canAuto) sendOtp(); }, [resetKey, canAuto]); // eslint-disable-line react-hooks/exhaustive-deps

  const redeem = () => {
    if (otp.trim().length !== 6) return toast.error("Inserisci il codice OTP a 6 cifre");
    return run(async () => {
      const r = await api.redeemReceipt(receipt.receipt_serial, otp.trim());
      onRedeemed(r);
      setOtpStep("idle");
      toast.success(`Premio ritirato presso ${store.name} · ricevuta di ritiro inviata via email`);
    }, "Operazione non riuscita");
  };

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3" data-testid="otp-panel">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <ShieldCheck className="h-4 w-4 text-amber-400" /> Conferma ritiro con codice OTP
      </div>
      {otpStep === "idle"
        ? <OtpRequest receipt={receipt} otpEmail={otpEmail} setOtpEmail={setOtpEmail} busy={busy} sendOtp={sendOtp} haveOtp={haveOtp} />
        : <OtpEntry otpInfo={otpInfo} otp={otp} setOtp={setOtp} busy={busy} redeem={redeem} sendOtp={sendOtp} />}
    </div>
  );
}
